import type { ReactNode } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { isActiveJob, resolveFormattingLocale, type Job } from '@eis/core';

export interface JobProgressProps {
  readonly job: Job;
}

/** processed / total of a job, as numbers (the wire carries int64 as strings). */
export function jobProgressOf(job: Job): { processed: number; total: number } {
  const processed = Number(job.progress?.processed ?? 0) || 0;
  const total = Number(job.progress?.total ?? 0) || 0;
  return { processed, total };
}

/**
 * The progress line of one job (SDD-020 §9), shared by the jobs tray and the import wizard: a
 * determinate bar with "3 500 von 5 000" once the worker knows the total, an indeterminate one
 * while the job waits or counts. Renders nothing for a finished job — its state chip says it.
 */
export function JobProgress({ job }: JobProgressProps): ReactNode {
  const { t, i18n } = useTranslation('patterns');
  if (!isActiveJob(job)) return null;
  const { processed, total } = jobProgressOf(job);
  const number = new Intl.NumberFormat(resolveFormattingLocale(i18n.language));
  const known = total > 0 && job.state === 'RUNNING';
  const label = t('jobs.progressLabel', { label: job.label ?? '' });
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      {known ? (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (processed / total) * 100)}
          aria-label={label}
        />
      ) : (
        <LinearProgress aria-label={label} />
      )}
      <Typography variant="caption" color="text.secondary">
        {known
          ? t('jobs.progress', { processed: number.format(processed), total: number.format(total) })
          : t(`jobs.state.${job.state ?? 'QUEUED'}`, { defaultValue: job.state ?? '' })}
      </Typography>
    </Stack>
  );
}
